import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { ShareModal } from '../components/ShareModal';
import { useConfirm } from '../components/ConfirmProvider';
import { useUserOrg } from '../lib/org';
import { getPermit, getSite, closePermit, cancelPermit, type PermitRow, type Site } from '../lib/db';
import { uploadPermit, getSignedUrl, getShareUrl } from '../lib/storage';
import { supabase } from '../lib/supabase';
import { PERMIT_TEMPLATES } from '../data/permitTemplates';
import { generatePermitPdf } from '../lib/pdf';
import { LoadingIndicator } from '../components/LoadingIndicator';

async function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

export function PermitView() {
  const { permitId } = useParams<{ permitId: string }>();
  const nav = useNavigate();
  const { org } = useUserOrg();
  const { confirm } = useConfirm();
  const closingSigRef = useRef<SignaturePadHandle>(null);

  const [permit, setPermit] = useState<PermitRow | null>(null);
  const [site, setSite] = useState<Site | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareNotice, setShareNotice] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareJustCreated, setShareJustCreated] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const [showCloseForm, setShowCloseForm] = useState(false);
  const [completionChecks, setCompletionChecks] = useState<boolean[]>([]);
  const [closingNotes, setClosingNotes] = useState('');
  const [closedByName, setClosedByName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!permitId) return;
    setLoading(true);
    (async () => {
      try {
        const p = await getPermit(permitId);
        if (!p) { setError('Permit not found.'); setLoading(false); return; }
        setPermit(p);
        if (p.site_id) setSite(await getSite(p.site_id));
        if (p.file_path) {
          const url = await getSignedUrl('permits', p.file_path, 3600);
          setPdfUrl(url);
        }
        const tmpl = PERMIT_TEMPLATES.find((t) => t.id === p.template_id);
        if (tmpl) setCompletionChecks(tmpl.completionChecks.map(() => false));
        const { data } = await supabase.auth.getUser();
        const name = (data.user?.user_metadata?.full_name as string | undefined) ?? '';
        if (name) setClosedByName(name);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [permitId]);

  // Auto-open the share modal when we land here from PermitFillForm with ?share=1.
  useEffect(() => {
    if (searchParams.get('share') === '1' && permit) {
      setShareJustCreated(true);
      setShareOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('share');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, permit]);

  if (loading) return <AppShell title="Loading…" back="/"><LoadingIndicator /></AppShell>;
  if (!permit) return <AppShell title="Permit" back="/"><div className="banner banner-error">{error ?? 'Not found.'}</div></AppShell>;

  const template = PERMIT_TEMPLATES.find((t) => t.id === permit.template_id);
  const allChecksTicked = completionChecks.every(Boolean);
  const isOpen = ['open', 'active'].includes(permit.status);

  async function copyShareLink() {
    if (!permit?.file_path) return;
    try {
      const url = await getShareUrl('permits', permit.file_path, 7 * 24 * 60 * 60);
      if (navigator.clipboard) await navigator.clipboard.writeText(url);
      setShareNotice('Share link copied — valid for 7 days.');
      setTimeout(() => setShareNotice(null), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function openShareModal() {
    setShareJustCreated(false);
    setShareOpen(true);
  }

  async function onClose(e: FormEvent) {
    e.preventDefault();
    if (!org || !permit || !site || !template) return;
    if (!closedByName.trim()) { setError('Closing signatory is required.'); return; }
    if (!allChecksTicked) { setError('All completion checks must be ticked before closing.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      let closingSig: string | null = null;
      if (closingSigRef.current && !closingSigRef.current.isEmpty()) {
        closingSig = await blobToDataUrl(await closingSigRef.current.toBlob());
      }

      // Re-render the PDF with closing block (we don't have the original signature; we reissue
      // using the data fields we have; the issued signature is omitted on the closed PDF).
      const blob = await generatePermitPdf({
        orgName: org.org_name,
        orgLogoPath: org.logo_path,
        title: permit.title,
        permitType: permit.permit_type,
        siteName: site.name,
        issuedToName: permit.issued_to_name,
        issuedToCompany: permit.issued_to_company,
        issuedByName: '—',
        issuedAt: new Date(permit.issued_at).toLocaleString('en-GB'),
        expiresAt: new Date(permit.expires_at).toLocaleString('en-GB'),
        description: template.description,
        prerequisites: template.prerequisites,
        controls: template.controls,
        signOffChecks: template.signOffChecks.map((label) => ({ label, checked: true })),
        fireWatchMinutes: permit.fire_watch_minutes,
        signatureDataUrl: null,
        notes: permit.notes,
        closing: {
          completionChecks: template.completionChecks.map((label, i) => ({ label, checked: completionChecks[i] ?? false })),
          closedByName: closedByName.trim(),
          closedAt: new Date().toLocaleString('en-GB'),
          closingSignatureDataUrl: closingSig,
          closingNotes: closingNotes.trim() || undefined,
        },
      });
      const fileName = (permit.file_name || `${permit.title}.pdf`).replace(/\.pdf$/i, '-closed.pdf');
      const file = new File([blob], fileName, { type: 'application/pdf' });
      await uploadPermit(org.org_id, permit.id, file); // upserts to same path

      await closePermit(permit.id, { notes: (permit.notes ? permit.notes + '\n\n' : '') + 'CLOSED — ' + (closingNotes.trim() || 'work complete.') });
      nav(`/sites/${site.id}/permits`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  async function onCancel() {
    if (!permit) return;
    if (!(await confirm({
      title: 'Cancel this permit?',
      body: 'Use this only if the work did not go ahead.',
      confirmLabel: 'Cancel permit',
      danger: true,
    }))) return;
    try {
      await cancelPermit(permit.id);
      if (site) nav(`/sites/${site.id}/permits`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <AppShell title={permit.title} back={site ? `/sites/${site.id}/permits` : '/'}>
      {error && <div className="banner banner-error">{error}</div>}
      {shareNotice && <div className="banner banner-success">{shareNotice}</div>}

      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0 }}><strong>{permit.title}</strong> · <span className={`status-pill status-${isOpen ? 'warn' : 'ok'}`}>{permit.status.toUpperCase()}</span></p>
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
          Issued to <strong>{permit.issued_to_name}</strong>{permit.issued_to_company ? ` (${permit.issued_to_company})` : ''}
        </p>
        <p className="muted" style={{ margin: '4px 0 0', fontSize: 12 }}>
          Issued {new Date(permit.issued_at).toLocaleString('en-GB')}
          {' · '}expires {new Date(permit.expires_at).toLocaleString('en-GB')}
          {permit.closed_at && ` · closed ${new Date(permit.closed_at).toLocaleString('en-GB')}`}
        </p>
      </div>

      <div className="btn-stack">
        {pdfUrl && (
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
            Open permit PDF
          </a>
        )}
        <button type="button" className="btn btn-ghost" onClick={copyShareLink}>Copy share link (7 days)</button>
        <button type="button" className="btn btn-ghost" onClick={openShareModal}>📨 Email this permit</button>
        {isOpen && !showCloseForm && (
          <button type="button" className="btn btn-primary" onClick={() => setShowCloseForm(true)}>
            Close permit
          </button>
        )}
        {isOpen && (
          <button type="button" className="btn btn-ghost btn-danger" onClick={onCancel}>
            Cancel permit
          </button>
        )}
      </div>

      {showCloseForm && template && (
        <form onSubmit={onClose} style={{ marginTop: 22 }}>
          <h3 className="section-title">Close-out checks</h3>
          <p className="muted" style={{ margin: '0 0 12px', fontSize: 13 }}>
            Tick each item before closing. Closure is recorded on the PDF.
          </p>
          <div className="check-list">
            {template.completionChecks.map((label, i) => (
              <label key={i} className="check-row">
                <input
                  type="checkbox"
                  checked={completionChecks[i] ?? false}
                  onChange={(e) => {
                    const next = completionChecks.slice();
                    next[i] = e.target.checked;
                    setCompletionChecks(next);
                  }}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>

          <div className="field" style={{ marginTop: 16 }}>
            <label htmlFor="cl-notes">Closing notes (optional)</label>
            <textarea id="cl-notes" rows={3} value={closingNotes} onChange={(e) => setClosingNotes(e.target.value)} />
          </div>

          <div className="field needs-input">
            <label htmlFor="cl-by">Closed by *</label>
            <input id="cl-by" type="text" value={closedByName} onChange={(e) => setClosedByName(e.target.value)} required />
          </div>

          <SignaturePad ref={closingSigRef} label="Sign as closer" />

          <div className="btn-stack">
            <button type="submit" className="btn btn-primary" disabled={submitting || !allChecksTicked}>
              {submitting ? 'Closing…' : 'Confirm closure'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setShowCloseForm(false)}>
              Back
            </button>
          </div>
        </form>
      )}

      {permit.file_path && (
        <ShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          docTitle={permit.title}
          docType="Permit"
          orgName={org?.org_name ?? undefined}
          siteName={site?.name ?? undefined}
          introCopy={
            shareJustCreated
              ? 'Permit issued. Send a copy to the contractor doing the work, the main contractor, anyone supervising.'
              : undefined
          }
          getShareUrl={() => getShareUrl('permits', permit.file_path!, 7 * 24 * 60 * 60)}
        />
      )}
    </AppShell>
  );
}
